import * as React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Today from '@mui/icons-material/Today';
import Task from '@mui/icons-material/Task';
import {
  Box,
  InputAdornment,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';

const TaskDefinitionAutocomplete = ({
  label,
  onSelect,
  value,
  validation,
  exludeTagIds,
  includeHorseTaskTypeTagId,
  includeBarnTaskTypeTagId,
  includeEventTaskTypeTagId,
  eventTemplates,
  limitTags,
  size,
  disabled,
  multiple,
  icon,
  placeholder,
  helperText,
  ...rest
}) => {
  // React state
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    setSearch('')
  }, [value])

  // Redux state
  const allEventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);

  let filteredEventTemplates = eventTemplates || allEventTemplates || [];
  if (!includeHorseTaskTypeTagId) {
    filteredEventTemplates = filteredEventTemplates
      .filter((et) => et.tags.map((t) => t.id).indexOf(selectedBarn.barnMetadata.horseTaskTypeTagId) < 0);
  }
  if (!includeBarnTaskTypeTagId) {
    filteredEventTemplates = filteredEventTemplates
      .filter((et) => et.tags.map((t) => t.id).indexOf(selectedBarn.barnMetadata.barnTaskTypeTagId) < 0);
  }
  if (!includeEventTaskTypeTagId) {
    filteredEventTemplates = filteredEventTemplates
      .filter((et) => et.tags.map((t) => t.id).indexOf(selectedBarn.barnMetadata.eventTaskTypeTagId) < 0);
  }

  const adornmentIcon = icon && (includeEventTaskTypeTagId && !includeBarnTaskTypeTagId && !includeHorseTaskTypeTagId)
    ? <Today />
    : <Task sx={{ color: (value && value.eventDataDefaultValues && value.eventDataDefaultValues.calendarDisplayColor) || '#A9A9A9' }} />

  return (
    <Autocomplete
      id="event-template-select-autocomplete"
      multiple={multiple}
      onChange={onSelect}
      disableCloseOnSelect={multiple}
      size={size}
      onBlur={() => setSearch('')}
      disableClearable={multiple}
      filterOptions={(x) => x}
      clearOnBlur={!multiple}
      {...rest}
      limitTags={limitTags}
      options={filteredEventTemplates.filter((et) => et.name.toLowerCase().indexOf(search.toLowerCase()) >= 0
        && exludeTagIds.indexOf(et.id) < 0).sort((a, b) => -(b.name).localeCompare(a.name))}
      value={value}
      disabled={disabled}
      isOptionEqualToValue={(option, valueCompare) => option.id === valueCompare.id}
      getOptionLabel={(option) => option.name}
      renderOption={(props, option) => (
        <MenuItem
          {...props}
          sx={{
            width: '100%',
            borderLeft: `6px solid ${(option.eventDataDefaultValues && option.eventDataDefaultValues.calendarDisplayColor) || '#A9A9A9'}`,
            borderBottom: `1px solid ${blueGrey[500]}`,
          }}
        >
          <Box sx={{ mr: 1 }}>
            {
              option.tags.find((t) => t.id === selectedBarn.barnMetadata.eventTaskTypeTagId)
                ? (
                  <Today sx={{ color: (option.eventDataDefaultValues && option.eventDataDefaultValues.calendarDisplayColor) || '#A9A9A9' }} />
                )
                : (
                  <Task sx={{ color: (option.eventDataDefaultValues && option.eventDataDefaultValues.calendarDisplayColor) || '#A9A9A9' }} />
                )
            }
          </Box>
          <ListItemText
            component="div"
            primary={option.name}
          // secondaryTypographyProps={{ component: 'div' }}
          // secondary={<TagList tags={option.tags} />}
          />
        </MenuItem>
      )}
      renderTags={(v, getTagProps) => v.map((option, index) => (
        <Chip
          key={`et-chip-autocomplete-${option.id}-${label}`}
          {...getTagProps({ index })}
          size={size}
          label={option.name}
        />
      ))}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          onChange={(e) => { setSearch(e.target.value); }}
          placeholder={placeholder || 'Search...'}
          error={!!validation}
          helperText={(validation && validation) || helperText}
          InputProps={{
            ...(params.InputProps || {}),
            startAdornment: params.InputProps.startAdornment || (icon && (
              <InputAdornment position="start">
                {adornmentIcon}
              </InputAdornment>
            )),
          }}
        />
      )}
    />
  );
};

TaskDefinitionAutocomplete.propTypes = {
  label: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.any,
  validation: PropTypes.string,
  eventTemplates: PropTypes.array,
  exludeTagIds: PropTypes.array,
  includeHorseTaskTypeTagId: PropTypes.bool,
  includeBarnTaskTypeTagId: PropTypes.bool,
  includeEventTaskTypeTagId: PropTypes.bool,
  size: PropTypes.string,
  limitTags: PropTypes.number,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  icon: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
};

TaskDefinitionAutocomplete.defaultProps = {
  label: 'Task',
  value: null,
  validation: null,
  eventTemplates: [],
  exludeTagIds: [],
  includeHorseTaskTypeTagId: true,
  includeBarnTaskTypeTagId: true,
  includeEventTaskTypeTagId: false,
  size: 'default',
  disabled: false,
  limitTags: 5,
  multiple: false,
  icon: false,
  placeholder: null,
  helperText: null,
};

export default TaskDefinitionAutocomplete;
